/** Which "complete when" and "review if" texts the risk register refuses (ADR-0230, D-09, G-P8-33).
 *
 * **A mirror with a witness, not a second opinion.** The server's rule is
 * `backend/app/modules/transition/wording.py` and stays the authority; this is the same check said
 * before the request instead of after it, so the sentence stands beside the box. `riskWording.test.ts`
 * reads that file and fails when the list or the floor differ, so the two cannot drift.
 */

/** What a person types to get past a required box. Compared after `normalise`. */
export const PLACEHOLDERS: readonly string[] = [
  "tbc",
  "tbd",
  "n/a",
  "na",
  "x",
  "-",
  "?",
  "none",
  "tba",
  "to be confirmed",
];

/** The shortest completion criterion accepted: the same floor as a stated basis. */
export const MIN_CRITERIA_CHARS = 12;

/** Trimmed, inner whitespace collapsed, case folded, and a closing full stop dropped. */
export function normalise(text: string): string {
  return text
    .trim()
    .split(/\s+/)
    .join(" ")
    .toLowerCase()
    .replace(/\.+$/, "")
    .trim();
}

export function isPlaceholder(text: string): boolean {
  return PLACEHOLDERS.includes(normalise(text));
}

/** The sentence refusing a completion criterion, or `null` when it is acceptable. */
export function criteriaRefusal(text: string): string | null {
  const stripped = text.trim();
  if (stripped === "") return "Say what would make this response complete. A response nobody can ever close stays open in every report from now on.";
  if (isPlaceholder(stripped)) {
    return (
      "That is a placeholder, not a completion test. Say what has to be true for this response to be " +
      `done (at least ${MIN_CRITERIA_CHARS} characters; TBC, TBD, n/a and similar are not accepted).`
    );
  }
  if ([...stripped].length < MIN_CRITERIA_CHARS) {
    return (
      `Say what would make this response complete in at least ${MIN_CRITERIA_CHARS} characters. ` +
      `This has ${[...stripped].length}.`
    );
  }
  return null;
}

/** The sentence refusing a review trigger, or `null`: the placeholder list only. */
export function triggerRefusal(text: string): string | null {
  const stripped = text.trim();
  if (stripped === "") return "Say what would make this assessment wrong, such as a new flood map. A review date with no trigger is a diary entry.";
  if (isPlaceholder(stripped)) {
    return (
      "That is a placeholder, not a trigger. Say what would make this assessment wrong " +
      "(TBC, TBD, n/a and similar are not accepted)."
    );
  }
  return null;
}
